import DownloadListAzureData from '@/components/DownloadListAzureData'
import Icon from '@/components/Icon'
import { Loading } from '@/components/Loading'
import RichText from '@/components/RichText'
import UnknownComponent from '@/components/UnknownComponent'
import { WrapperWithInView } from '@/components/WrapperWithInView'
import { ButtonSimple } from '@/components/nsw/button/ButtonSimple'
import { NswFilter } from '@/components/nsw/filters/NswFilter'
import { NswFilterCancel } from '@/components/nsw/filters/NswFilterCancel'
import { NswFilterItem } from '@/components/nsw/filters/NswFilterItem'
import { NswFilterList } from '@/components/nsw/filters/NswFilterList'
import { NswFormFieldset } from '@/components/nsw/filters/NswFormFieldset'
import { NswResultBar } from '@/components/nsw/filters/NswResultBar'
import { GridCol } from '@/components/nsw/grid/GridCol'
import { GridWrapper } from '@/components/nsw/grid/GridWrapper'
import { TagListTreeMultiSelectNode } from '@/components/tree-multi-select/TagListTreeMultiSelectNode'
import { TagListTreeMultiSelectWrapper } from '@/components/tree-multi-select/TagListTreeMultiSelectWrapper'
import { TreeMultiSelect } from '@/components/tree-multi-select/TreeMultiSelect'
import { CommonCopyUrlWrapper } from '@/components/ui/copy-to-clipboard/CommonCopyUrlWrapper'
import { DEFAULT_NUQS_TRANSITION_OPTIONS, STAGE_YEARS } from '@/constants'
import { COMMON_PAGE_SIZE_OPTIONS } from '@/constants/teaching_advice_resource'
import { type WpResourcesResponseData } from '@/databuilders/wp_resources'
import { fetchTeachingLearningSupport } from '@/fetchers/fetchTeachingLearningSupport'
import { fetchResources } from '@/fetchers/fetchTeachingResources'
import { useCleanPathDefault } from '@/hooks/useCleanPathDefault'
import { useCommonDropdownChange } from '@/hooks/useCommonDropdownChange'
import { useDownloadSelectedFiles } from '@/hooks/useDownloadSelectedFiles'
import { useIsScreenDown } from '@/hooks/useIsScreenDown'
import { useKlaTreeNodeProps } from '@/hooks/useKlaTreeNodeProps'
import { useResourceTypeTreeNodeProps } from '@/hooks/useResourceTypeTreeNodeProps'
import { useStageTreeNodeProps } from '@/hooks/useStageTreeNodeProps'
import { useToggle } from '@/hooks/useToggle'
import { useTreeMultiSelectSelected } from '@/hooks/useTreeMultiSelectSelected'
import type { WpResources as WpResourcesModel } from '@/kontent/content-types/wp_resources'
import { TaxoKeyLearningArea, TaxoResourceType } from '@/kontent/taxonomies'
import { TaxoStage } from '@/kontent/taxonomies/stage'
import { TaxoSyllabus } from '@/kontent/taxonomies/syllabus'
import CustomModal from '@/legacy-ported/components/base/CustomModal'
import type { ISelectOption } from '@/legacy-ported/components/base/CustomSelect'
import SearchBar from '@/legacy-ported/components/base/SearchBar'
import GeneratingOverlayCommon from '@/legacy-ported/components/document/overlay/GeneratingOverlayCommon'
import type {
	CommonPageProps,
	TaxoStageWithLifeSkill,
	TaxoStageYearWithLifeSkill,
} from '@/types'
import {
	DownloadListAzureDataProps,
	DownloadListAzureField,
} from '@/types/downloadListAzureData'
import {
	FetchLearningSupportsParams,
	FetchLearningSupportsResponse,
	FetchLearningSupportsResponseData,
} from '@/types/fetchLearningSupportFromAzure'
import {
	FetchResourcesParams,
	FetchResourcesResponse,
	FetchResourcesResponseData,
} from '@/types/fetchResourcesFromAzure'
import { getArrayLength, isRichtextElementEmpty } from '@/utils'
import { CommonFetchResponse } from '@/utils/fetchUtils'
import { getSelectedNodesFromData } from '@/utils/tree-multi-select'
import type { ElementModels } from '@kontent-ai/delivery-sdk'
import { useQuery } from '@tanstack/react-query'
import animateScrollTo from 'animated-scroll-to'
import clsx from 'clsx'
import { queryTypes, useQueryStates } from 'next-usequerystate'
import { Alert, Button } from 'nsw-ds-react'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { TreeNodeProps } from 'react-dropdown-tree-select'

export function convertTaxonomyTermsIntoSelectOption<TTerm extends string>(
	taxonomyTerms: ElementModels.TaxonomyTerm<TTerm>[],
): ISelectOption[] {
	return taxonomyTerms.map((term) => {
		return {
			text: term.name,
			value: term.codename,
		}
	})
}

// Type unions to support both Teaching Resources and Teaching & Learning Support APIs
type ResourceApiResponse =
	| CommonFetchResponse<FetchResourcesResponse>
	| CommonFetchResponse<FetchLearningSupportsResponse>
type ResourceApiResponseData =
	| FetchResourcesResponseData
	| FetchLearningSupportsResponseData

/**
 * Transforms parameters for Teaching & Learning Support API
 * Converts array syllabuses to single syllabus and keyLearningAreas to keylearningareas
 */
function transformToLearningSupportParams(
	params: Omit<FetchResourcesParams, 'keyLearningAreas' | 'syllabuses'> & {
		syllabuses: TaxoSyllabus[]
		keyLearningAreas: TaxoKeyLearningArea[]
	},
): Omit<FetchLearningSupportsParams, 'preview'> {
	return {
		syllabus: (params.syllabuses[0] || '') as TaxoSyllabus, // Take first syllabus
		keylearningareas: params.keyLearningAreas, // Rename to lowercase
		stages: params.stages,
		years: params.years,
		keywords: params.keywords,
		codenames: params.codenames,
		page: params.page,
		page_size: params.page_size,
		resourcetypes: params.resourcetypes,
		sort_by: params.sort_by,
	}
}

function WpResources(
	props: CommonPageProps<WpResourcesModel, WpResourcesResponseData> & {
		// by default, the filter is shown
		defaultFilterShown?: boolean
		preSelectedSyllabuses?: TaxoSyllabus[]
		hideSyllabusFilter?: boolean
		includeStages?: TaxoStageWithLifeSkill[]
		getHiddenColumns?: (
			_isFilterShown: boolean,
			_isScreenDownLg: boolean,
		) => DownloadListAzureField[]
		hideStageGroupsInStageFilter?: boolean
		hideStageFilter?: boolean
		showYearFilter?: boolean
		titleTag?: string
		pageType?: string
		hideFilterQuery?: boolean
		useLearningSupportApi?: boolean
	},
) {
	const isScreenDownLg = useIsScreenDown('lg')
	const {
		preview,
		defaultFilterShown = true,
		preSelectedSyllabuses,
		hideSyllabusFilter,
		includeStages,
		getHiddenColumns,
		hideStageGroupsInStageFilter,
		hideStageFilter,
		showYearFilter,
		titleTag = 'h1',
		pageType = 'Teaching resources',
		hideFilterQuery = false,
		useLearningSupportApi = false,
	} = props
	const {
		config,
		pageResponse,
		keyLearningAreas,
		stages,
		stageGroups,
		syllabuses,
	} = props.data
	const refFirstLoad = useRef(true)

	const [qs, setQS] = useQueryStates({
		q: queryTypes.string.withDefault(''), // search query
		t: queryTypes.string.withDefault(''), //resource type
		s: queryTypes.string.withDefault(''), // syllabuses, e.g. english_advanced_11_12_2024,english_eald_11_12_2024
		ps: queryTypes.integer.withDefault(COMMON_PAGE_SIZE_OPTIONS[1]), // page size,
		stage: queryTypes.string.withDefault(''), // stages, e.g. life_skills@primary___stage_3
		year: queryTypes.string.withDefault(''), // years, e.g. 2024
		p: queryTypes.integer.withDefault(1), // page number
		code: queryTypes.string.withDefault(''),
		sort: queryTypes.string.withDefault(''),
	})

	const selectedQsSyllabus = preSelectedSyllabuses
		? preSelectedSyllabuses
		: qs.s
		? qs.s.split(',')
		: []
	const selectedQsStage = qs.stage ? qs.stage.split(',') : []
	const selectedQsYear = qs.year ? qs.year.split(',') : []
	const selectedQsResourceType = qs.t ? qs.t.split(',') : []
	const selectedSort = useMemo(
		() =>
			qs.sort
				? (qs.sort.split(',').map((sortModelStr) => {
						//sort model str could be 'field_name_sort', e.g. 'resource_type_asc'
						// get direction
						const direction = sortModelStr.split('_').pop()
						// get field
						const field = sortModelStr.replace(`_${direction}`, '')

						return { field, direction }
				  }) as FetchResourcesParams['sort_by'])
				: [],
		[qs.sort],
	)
	const selectedSortModel = useMemo<
		DownloadListAzureDataProps['initialSortModel']
	>(() => {
		return selectedSort.length
			? selectedSort.map((sort) => ({
					field: sort.field as DownloadListAzureField,
					sort: sort.direction,
			  }))
			: [
					{
						field: 'officialreleasedate' as DownloadListAzureField,
						sort: 'desc',
					},
			  ]
	}, [selectedSort])

	const _currentUrl = useCleanPathDefault()
	const currentUrl = useMemo(() => {
		return _currentUrl
	}, [_currentUrl, qs])

	const qPageInt = qs.p ? qs.p : 1
	const page = pageResponse.item
	const refScrollHereAfterFilter = useRef<HTMLDivElement>(null)

	const klaOptions = useKlaTreeNodeProps({
		// CMS field marked for deletion (renamed to del_*) - passing undefined pending cleanup
		// disabledKlas: config.item.elements.disabled_key_learning_areas,
		disabledKlas: undefined,
		keyLearningAreas: !preSelectedSyllabuses ? keyLearningAreas : [],
		syllabuses: !preSelectedSyllabuses ? syllabuses.items : [],
		selectedSyllabus: selectedQsSyllabus as TaxoSyllabus[],
	})

	const stageOptions = useStageTreeNodeProps({
		stageGroups,
		stages: stages.filter((stage) => {
			if (includeStages) {
				return includeStages.includes(stage.codename)
			}
			return true
		}),
		selectedStages: selectedQsStage as TaxoStage[],
		withLifeSkillsOnSecondaryAndSenior: true,
		hideStageGroupsInStageFilter,
	})

	const yearOptions = useMemo<TreeNodeProps[]>(() => {
		return STAGE_YEARS.stage_6.map((year) => {
			return {
				value: year,
				label: year.replace('n', 'Year '),
				checked: selectedQsYear.includes(year),
				children: [],
			}
		})
	}, [selectedQsYear])

	const resourceTypeOptions = useResourceTypeTreeNodeProps({
		selectedResourceTypes: selectedQsResourceType.filter(
			Boolean,
		) as TaxoResourceType[],
	})

	// from the klaOptions, find the child nodes that are selected. Match with selectedQsSyllabus
	const selectedKlaOptionsChildNodes = klaOptions
		.flatMap((kla) => {
			return kla.children.flatMap((child) => {
				if (selectedQsSyllabus.includes(child.value)) {
					return child
				}
				return null
			})
		})
		.filter(Boolean)

	const selectedStageOptionsChildNodes = stageOptions
		.flatMap((stage) => {
			return stage.children.flatMap((child) => {
				if (selectedQsStage.includes(child.value)) {
					return child
				}
				return null
			})
		})
		.filter(Boolean)

	const selectedYearOptionsChildNodes = useMemo(() => {
		return yearOptions.filter((year) => {
			return selectedQsYear.includes(year.value)
		})
	}, [yearOptions, selectedQsYear])

	const selectedResourceTypeOptionNodes = resourceTypeOptions
		.map((resourceType) => {
			return resourceType.value
		})
		.filter((resourceType) =>
			selectedQsResourceType.includes(resourceType as TaxoResourceType),
		)

	const {
		selectedChildNodesValues: selectedFilterSyllabus,
		handleDrodpownChange: handleSyllabusesFilterChange,
		handleRemoveNode: handleRemoveNodeSyllabus,
		handleReset: handleResetSyllabus,
	} = useTreeMultiSelectSelected<TaxoSyllabus>({
		initialSelectedChildNodes: selectedKlaOptionsChildNodes,
		additionalFnOnDropdownChange: useCommonDropdownChange({
			setQS,
			queryKey: 's',
		}),
	})

	const {
		selectedChildNodesValues: selectedFilterStagesChildNodes,
		selectedNodesValues: selectedFilterStagesNodes,
		handleDrodpownChange: handleStagesFilterChange,
		handleRemoveNode: handleRemoveNodeStage,
		handleReset: handleResetStages,
	} = useTreeMultiSelectSelected<TaxoStage>({
		initialSelectedChildNodes: selectedStageOptionsChildNodes,
		additionalFnOnDropdownChange: useCommonDropdownChange({
			setQS,
			queryKey: 'stage',
			useParentNodes: hideStageGroupsInStageFilter,
		}),
	})

	const selectedFilterStages = hideStageGroupsInStageFilter
		? selectedFilterStagesNodes
		: selectedFilterStagesChildNodes

	const {
		selectedNodesValues: selectedFilterYears,
		handleDrodpownChange: handleYearsFilterChange,
		handleRemoveNode: handleRemoveNodeYear,
		handleReset: handleResetYears,
	} = useTreeMultiSelectSelected<TaxoStageYearWithLifeSkill>({
		initialSelectedNodes: selectedYearOptionsChildNodes,
		additionalFnOnDropdownChange: useCommonDropdownChange({
			setQS,
			queryKey: 'year',
			useParentNodes: true,
		}),
	})

	const {
		selectedNodesValues: selectedFilterResourceTypes,
		handleDrodpownChange: handleResourceTypesFilterChange,
		handleRemoveNode: handleRemoveNodeResourceType,
		handleReset: handleResetResourceTypes,
	} = useTreeMultiSelectSelected<TaxoResourceType>({
		initialSelectedNodes: selectedResourceTypeOptionNodes,
		additionalFnOnDropdownChange: useCommonDropdownChange({
			setQS,
			queryKey: 't',
			useParentNodes: true,
		}),
	})

	const [isFilterShown, toggleIsFilterShown] = useToggle(defaultFilterShown)

	const {
		data: resourcesResponse,
		isFetchedAfterMount: isFetchedResources,
		isFetching: isFetchingResources,
	} = useQuery({
		queryKey: [
			useLearningSupportApi ? 'learning-support' : 'teaching-resources',
			qs.q,
			selectedQsSyllabus,
			selectedQsStage,
			selectedQsYear,
			qs.ps,
			qs.p,
			qs.t,
			qs.sort,
		],
		queryFn: async () => {
			const baseParams = {
				keywords: qs.q || null,
				syllabuses: selectedQsSyllabus as TaxoSyllabus[],
				keyLearningAreas: [],
				page_size: qs.ps || null,
				page: qs.p || null,
				stages: selectedQsStage as TaxoStageWithLifeSkill[],
				years: selectedQsYear as TaxoStageYearWithLifeSkill[],
				resourcetypes: selectedQsResourceType as TaxoResourceType[],
				sort_by: selectedSort,
				preview,
			}

			if (useLearningSupportApi) {
				// Validate single syllabus requirement for Learning Support API
				if (baseParams.syllabuses.length === 0) {
					console.warn(
						'Teaching & Learning Support API requires a syllabus. Using empty string.',
					)
				} else if (baseParams.syllabuses.length > 1) {
					console.warn(
						`Teaching & Learning Support API expects a single syllabus. Using first syllabus: ${
							baseParams.syllabuses[0]
						}. Ignoring: ${baseParams.syllabuses
							.slice(1)
							.join(', ')}`,
					)
				}

				const learningSupportParams =
					transformToLearningSupportParams(baseParams)
				return fetchTeachingLearningSupport({
					...learningSupportParams,
					preview,
				})
			}

			return fetchResources(baseParams)
		},
		keepPreviousData: true,
		staleTime: 0,
	})

	const selectedFilterTagSyllabus = useMemo(() => {
		return getSelectedNodesFromData(klaOptions)
	}, [klaOptions])
	const selectedFilterTagStage = useMemo(() => {
		return getSelectedNodesFromData(stageOptions)
	}, [stageOptions])

	// Create syllabusContext for GTM tracking based on stage parameter
	const syllabusContext = useMemo(() => {
		const isLifeSkillMode = qs.stage.includes('life_skills@')
		return {
			isLifeSkills: isLifeSkillMode,
			isMainstream: !isLifeSkillMode,
		}
	}, [qs.stage])

	const filteredResources = useMemo(() => {
		return isFetchedResources ? resourcesResponse?.json?.data ?? [] : []
	}, [isFetchedResources, resourcesResponse?.json?.data])

	// Build filteredResourceTypeOptions by filtering static options based on API response
	const filteredResourceTypeOptions = useMemo<TreeNodeProps[]>(() => {
		// When API response is available and has resource_types with data, filter static options
		// Check for existence, array type, and non-empty array
		if (
			resourcesResponse?.json?.resource_types &&
			Array.isArray(resourcesResponse.json.resource_types) &&
			resourcesResponse.json.resource_types.length > 0
		) {
			// Create a Set of resource types that have RecordCount > 0 for efficient lookup
			const availableResourceTypes = new Set(
				resourcesResponse.json.resource_types
					.filter((rt) => rt.RecordCount > 0)
					.map((rt) => rt.ResourceType),
			)

			// Filter static resourceTypeOptions to only include matched types with RecordCount > 0
			const result = resourceTypeOptions.filter((option) =>
				availableResourceTypes.has(option.value as TaxoResourceType),
			)

			return result
		}

		// When API response not available yet or has no resource_types data, return empty array
		// This prevents showing options while waiting for dynamic API data
		return []
	}, [resourcesResponse?.json?.resource_types, resourceTypeOptions])

	// Build selected filter tags for resource types (must be after filteredResourceTypeOptions is defined)
	const selectedFilterTagResourceType = useMemo(() => {
		return getSelectedNodesFromData(filteredResourceTypeOptions)
	}, [filteredResourceTypeOptions])

	const simpleFilesOfFilteredResources = useMemo(() => {
		return filteredResources.map((resource) => {
			return {
				id: resource.system.id,
				title: resource.title,
				resource_type: resource.resource_type.codename,
				content_type: resource.system.type,
				url: resource.url,
				syllabuses: resource.syllabuses.map((s) => s.taxonomy),
				stages: resource.stages.map((s) => s.codename),
				years: resource.years?.map((y) => y.codename),
				syllabus_types: resource.syllabus_types.map(
					(st) => st.codename,
				),
				officialreleasedate:
					resource.officialreleasedate instanceof Date
						? resource.officialreleasedate.toISOString()
						: typeof resource.officialreleasedate === 'string'
						? resource.officialreleasedate
						: new Date(resource.officialreleasedate).toISOString(),
				filesize: resource.filesize,
			}
		})
	}, [filteredResources])

	const {
		errorMessageOnDownload,
		selectedAssetIds,
		isDownloading,
		maxFileReached,
		maxFileSizeInMB,
		setSelectedAssets,
		setErrorMessageOnDownload,
		handleDownloadSelected: originalHandleDownloadSelected,
		handleCancelDownloadSelected,
	} = useDownloadSelectedFiles({
		alertEl: refScrollHereAfterFilter.current,
		files: simpleFilesOfFilteredResources,
		downloadCustomResourceSyllabusName: 'resources',
		pageType,
		onSuccessfulDownload: () => {
			setSelectedAssets([])
		},
	})

	// Wrap the original handleDownloadSelected to clear selection after download
	const handleDownloadSelected = async () => {
		await originalHandleDownloadSelected()
	}

	const hiddenFields = useMemo<DownloadListAzureField[]>(() => {
		if (getHiddenColumns) {
			return getHiddenColumns(isFilterShown, isScreenDownLg)
		}
		const defaultHiddens: DownloadListAzureField[] = [
			'fileType',
			'year',
			'fileSize',
		]
		if (isScreenDownLg) {
			return [
				...defaultHiddens,
				'syllabuses',
				'resource_type',
				'stages',
				'officialreleasedate',
			]
		}

		return isFilterShown
			? [
					...defaultHiddens,
					'resource_type',
					'stages',
					'officialreleasedate',
			  ]
			: [...defaultHiddens, 'info']
	}, [isFilterShown, isScreenDownLg, getHiddenColumns])

	const scrollToTopOfRightCol = () => {
		if (refScrollHereAfterFilter.current) {
			animateScrollTo(refScrollHereAfterFilter.current, {
				speed: 0,
				verticalOffset: -24,
			})
		}
	}

	const resetPageAndScroll = useCallback(
		async (pageNumber = 0) => {
			// the `pageNumber` is 0-based
			scrollToTopOfRightCol()
			setQS(
				{
					p: pageNumber ? pageNumber + 1 : null,
				},
				DEFAULT_NUQS_TRANSITION_OPTIONS,
			)
		},
		[setQS],
	)

	// Methods
	// handle "Clear All"
	const handleClearAll = () => {
		// Reset URL query parameters
		// Do NOT reset ps (page size) or sort (sort order)
		setQS(
			{
				q: null,
				s: null,
				stage: null,
				year: null,
				t: null,
				p: null,
			},
			DEFAULT_NUQS_TRANSITION_OPTIONS,
		)

		// Reset internal states
		handleResetSyllabus()
		handleResetStages()
		handleResetYears()
		handleResetResourceTypes()
	}

	const handleSortModelChange = (sortModel) => {
		setQS(
			{
				sort: sortModel.map((s) => `${s.field}_${s.sort}`).join(','),
			},
			DEFAULT_NUQS_TRANSITION_OPTIONS,
		)
	}

	const handleRemoveTagClick = useCallback(
		(
				queryKey: string,
				selectedFilterState: (
					| TaxoStage
					| TaxoSyllabus
					| TaxoResourceType
					| TaxoStageYearWithLifeSkill
				)[],
				handleRemoveNodeFn: (_removedNode: TreeNodeProps) => void,
			) =>
			(_e, removedNode) => {
				// check if the removed node is a child node or a parent node
				// if it is a parent node, remove all the child nodes
				// if it is a child node, remove only the child node

				const _removedNodes =
					removedNode.children?.length > 0
						? removedNode.children.map((n) => n.value)
						: [removedNode.value]

				setQS(
					{
						[queryKey]:
							selectedFilterState
								.filter((l) => !_removedNodes.includes(l))
								.join(',') || null,
						p: null,
					},
					DEFAULT_NUQS_TRANSITION_OPTIONS,
				)
				handleRemoveNodeFn(removedNode)
			},
		[setQS],
	)

	useEffect(() => {
		if (isFetchedResources && !refFirstLoad.current) {
			scrollToTopOfRightCol()
		}
		if (isFetchedResources) {
			refFirstLoad.current = false
		}
	}, [isFetchedResources])

	if (!page) {
		return (
			<UnknownComponent>
				Page {page.system.codename} does not have any content!
			</UnknownComponent>
		)
	}

	const TitleTag = titleTag

	return (
		<>
			{page.elements.title.value && (
				<CommonCopyUrlWrapper url={currentUrl} className="mb-8">
					{/* @ts-ignore */}
					<TitleTag
						data-kontent-item-id={page.system.id}
						data-kontent-element-codename="title"
					>
						{page.elements.title.value}
					</TitleTag>
				</CommonCopyUrlWrapper>
			)}

			{!isRichtextElementEmpty(
				page.elements.web_content_rtb__content,
			) && (
				<RichText
					mappings={props.mappings}
					linkedItems={pageResponse.linkedItems}
					className="w-full cms-content-formatting max-w-3xl"
					richTextElement={page.elements.web_content_rtb__content}
				/>
			)}

			{/* search input */}
			<div ref={refScrollHereAfterFilter} className="my-8">
				<div className="flex flex-wrap gap-4 lg:gap-8">
					<div className="flex-1 basis-full lg:basis-0 max-w-3xl">
						<SearchBar
							initialSearchText={qs.q}
							onSearch={(text) => {
								setQS(
									{
										q: text || null,
										p: null,
									},
									DEFAULT_NUQS_TRANSITION_OPTIONS,
								)
							}}
							value={qs.q}
							disableResetSearchText
							debounceTime={300}
						/>
					</div>
					<label className="flex items-center gap-3 !mr-0">
						<span className="flex-shrink-0 nsw-form__label">
							Results per page:
						</span>
						<select
							className="nsw-form__select max-w-[200px] !mt-0"
							onChange={(e) => {
								const value = parseInt(e.target.value)
								setQS(
									{
										p: null,
										ps:
											value ===
											COMMON_PAGE_SIZE_OPTIONS[1]
												? null
												: value,
									},
									DEFAULT_NUQS_TRANSITION_OPTIONS,
								)
							}}
							value={qs.ps}
							autoComplete="off"
						>
							{COMMON_PAGE_SIZE_OPTIONS.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</select>
					</label>
				</div>
			</div>

			<div className="my-8">
				<GridWrapper className="!mt-0">
					<GridCol
						lg={3}
						className={clsx('!pt-0', {
							'lg:hidden': !isFilterShown,
						})}
					>
						<NswFilter
							css={{
								'& > .nsw-filters__controls': {
									borderBottom: 0,
									paddingTop: 0,
									paddingBottom: '2rem',
								},
								'& > .nsw-nsw-form__fieldset': {
									padding: 0,
								},
								'& .nsw-form__legend': {
									paddingBottom: 12,
								},
							}}
							totalItems={resourcesResponse?.json?.total_records}
							mobileToggleFilterLabel="Show filters"
							title={
								<ButtonSimple
									onClick={toggleIsFilterShown}
									slotBeforeChildren={
										<Icon
											icon="ic:baseline-close"
											width={24}
											height={24}
											className="block p-0.5 bg-nsw-brand-dark text-white rounded-[4px]"
										/>
									}
								>
									Hide filters
								</ButtonSimple>
							}
						>
							<NswFilterList>
								{!hideStageFilter && (
									<NswFilterItem>
										<NswFormFieldset title="Stages">
											<TreeMultiSelect
												key={stageOptions
													.map((r) => r.value)
													.join(',')}
												data={stageOptions}
												onChange={
													handleStagesFilterChange
												}
												placeholder="Select stages"
												texts={{
													label: 'Select stages',
												}}
												hideTagsSelected
											/>
										</NswFormFieldset>
									</NswFilterItem>
								)}
								{showYearFilter && (
									<NswFilterItem>
										<NswFormFieldset title="Years">
											<TreeMultiSelect
												key={yearOptions
													.map((r) => r.value)
													.join(',')}
												data={yearOptions}
												onChange={
													handleYearsFilterChange
												}
												placeholder="Select years"
												texts={{
													label: 'Select years',
												}}
												hideTagsSelected
											/>
										</NswFormFieldset>
									</NswFilterItem>
								)}

								{!hideSyllabusFilter && (
									<NswFilterItem>
										<NswFormFieldset title="Learning areas and syllabuses">
											<TreeMultiSelect
												key={klaOptions
													.map((r) => r.value)
													.join(',')}
												data={klaOptions}
												onChange={
													handleSyllabusesFilterChange
												}
												placeholder="Select syllabuses"
												texts={{
													label: 'Select syllabuses',
												}}
												hideTagsSelected
											/>
										</NswFormFieldset>
									</NswFilterItem>
								)}
								<NswFilterItem>
									<NswFormFieldset title="Syllabus support materials">
										<TreeMultiSelect
											key={filteredResourceTypeOptions
												.map((r) => r.value)
												.join(',')}
											data={filteredResourceTypeOptions}
											onChange={
												handleResourceTypesFilterChange
											}
											placeholder="Select resource type"
											texts={{
												label: 'Select resource type',
											}}
											hideTagsSelected
										/>
									</NswFormFieldset>
								</NswFilterItem>
							</NswFilterList>
							<NswFilterCancel onReset={handleClearAll} />
						</NswFilter>
					</GridCol>
					<GridCol lg={isFilterShown ? 9 : 12} className="!pt-0">
						<div className="relative lg:border-t border-nsw-grey-04">
							{(selectedFilterTagStage.length > 0 ||
								(!preSelectedSyllabuses &&
									selectedFilterTagSyllabus.length > 0) ||
								selectedFilterTagResourceType.length > 0 ||
								selectedFilterYears.length > 0) && (
								<TagListTreeMultiSelectWrapper
									className={clsx({
										'py-8 border-b border-nsw-grey-04':
											selectedFilterTagStage.length > 0 ||
											(!preSelectedSyllabuses &&
												selectedFilterTagSyllabus.length >
													0) ||
											selectedFilterTagResourceType.length >
												0 ||
											selectedFilterYears.length > 0,
									})}
									showClearButton={
										getArrayLength(selectedFilterTagStage) +
											(preSelectedSyllabuses
												? 0
												: getArrayLength(
														selectedFilterTagSyllabus,
												  )) +
											getArrayLength(
												selectedFilterTagResourceType,
											) +
											getArrayLength(
												selectedFilterYears,
											) >
										1
									}
									onClearClick={handleClearAll}
								>
									{selectedFilterTagStage.length > 0 && (
										<TagListTreeMultiSelectNode
											list={selectedFilterTagStage}
											onRemoveClick={handleRemoveTagClick(
												'stage',
												selectedFilterStages,
												handleRemoveNodeStage,
											)}
										/>
									)}
									{!preSelectedSyllabuses &&
										selectedFilterTagSyllabus.length >
											0 && (
											<TagListTreeMultiSelectNode
												list={selectedFilterTagSyllabus}
												onRemoveClick={handleRemoveTagClick(
													's',
													selectedFilterSyllabus,
													handleRemoveNodeSyllabus,
												)}
											/>
										)}
									{selectedFilterTagResourceType.length >
										0 && (
										<TagListTreeMultiSelectNode
											list={selectedFilterTagResourceType}
											onRemoveClick={handleRemoveTagClick(
												't',
												selectedFilterResourceTypes,
												handleRemoveNodeResourceType,
											)}
										/>
									)}
									{showYearFilter &&
										selectedFilterYears.length > 0 && (
											<TagListTreeMultiSelectNode
												list={yearOptions.filter(
													(year) =>
														selectedFilterYears.includes(
															year.value as TaxoStageYearWithLifeSkill,
														),
												)}
												onRemoveClick={handleRemoveTagClick(
													'year',
													selectedFilterYears,
													handleRemoveNodeYear,
												)}
											/>
										)}
								</TagListTreeMultiSelectWrapper>
							)}
							<NswResultBar
								className={clsx('flex-wrap gap-3 !pt-6')}
								css={{
									'&&': {
										marginTop: 0,
										alignItems: 'center',
									},
									'.NswResultBar__result': {
										display: 'flex',
										alignItems: 'center',
										gap: '1rem',
									},
									'.nsw-results-bar__info': {
										flex: '1 0 100%',
										display: 'flex',
										justifyContent: 'space-between',
										flexWrap: 'wrap',
										gap: 16,
									},
									'.nsw-results-bar__sorting': {
										paddingTop: 0,
										width: 1,
										overflow: 'hidden',
									},
								}}
								page={qPageInt}
								pageSize={resourcesResponse?.json?.page_size}
								total={resourcesResponse?.json?.total_records}
								slotShowingResultsAfter={
									resourcesResponse?.json?.total_records >
										0 && (
										<Button
											className="flex items-center gap-2 w-full lg:w-auto justify-center"
											onClick={handleDownloadSelected}
											disabled={
												!selectedAssetIds.length ||
												isDownloading ||
												maxFileReached
											}
										>
											<span className="mr-2">
												Download selected
												{selectedAssetIds.length > 0 &&
													` (${selectedAssetIds.length})`}
											</span>
											<Icon icon="bxs:download" />
										</Button>
									)
								}
								slotShowingResultsNumberBefore={
									!isFilterShown && (
										<ButtonSimple
											className={clsx(
												'border-r border-r-nsw-grey-03 pr-4',
												{
													'hidden lg:inline-flex':
														!defaultFilterShown,
												},
											)}
											onClick={toggleIsFilterShown}
											slotBeforeChildren={
												<Icon
													icon="mdi:tune"
													width={24}
													height={24}
													className="block p-0.5 bg-nsw-brand-dark text-white rounded-[4px]"
												/>
											}
										>
											Show filters
										</ButtonSimple>
									)
								}
							></NswResultBar>
							{resourcesResponse?.json?.data?.length > 0 ? (
								<>
									<div className="flex gap-3 align-top justify-between mb-4 flex-wrap md:flex-nowrap">
										<WrapperWithInView>
											{(inView) => (
												<>
													<div className="flex-shrink-0 md:ml-auto"></div>
													<div
														className={clsx(
															'fixed top-0 left-0 bg-white z-10 py-3 w-full shadow-[0_4px_12px_0_rgba(0,0,0,.3)] transition duration-300',
															{
																'pointer-events-none opacity-0':
																	inView ||
																	!selectedAssetIds.length,
																'top-[26px]':
																	preview,
															},
														)}
														aria-hidden={
															inView ||
															!selectedAssetIds.length
																? 'true'
																: 'false'
														}
													>
														<div className="nsw-container flex justify-end">
															<Button
																onClick={
																	handleDownloadSelected
																}
																disabled={
																	!selectedAssetIds.length ||
																	isDownloading ||
																	maxFileReached
																}
															>
																<span className="mr-2">
																	Download
																	selected
																	{selectedAssetIds.length >
																		0 &&
																		` (${selectedAssetIds.length})`}
																</span>
																<Icon icon="bxs:download" />
															</Button>
														</div>
													</div>
												</>
											)}
										</WrapperWithInView>
									</div>

									{maxFileReached && (
										<Alert
											title="Warning"
											as="warning"
											className="mb-3"
										>
											<p>
												Maximum of {maxFileSizeInMB} MB
												reached
											</p>
										</Alert>
									)}

									{isFetchedResources &&
										resourcesResponse?.json && (
											<DownloadListAzureData
												page={qPageInt - 1}
												pageType={pageType}
												pageSize={
													resourcesResponse.json
														.page_size
												}
												responseData={
													filteredResources as FetchResourcesResponseData[]
												}
												hiddenFields={hiddenFields}
												onSelectedFiles={(
													_selectedFiles,
												) => {
													setSelectedAssets(
														(prevFiles) => {
															// Get IDs of files on current page
															const currentPageIds =
																new Set(
																	filteredResources.map(
																		(f) =>
																			f
																				.system
																				.id,
																	),
																)

															// Keep previously selected files not on current page
															const remainingPrevFiles =
																prevFiles.filter(
																	(f) =>
																		!currentPageIds.has(
																			f.id,
																		),
																)

															// Get newly selected files from simpleFilesOfFilteredResources
															const newlySelectedFiles =
																_selectedFiles.map(
																	(file) => {
																		const matchingFile =
																			simpleFilesOfFilteredResources.find(
																				(
																					f,
																				) =>
																					f.id ===
																					file.id,
																			)

																		// check prevFiles.
																		const matchingPreviousFile =
																			prevFiles.find(
																				(
																					f,
																				) =>
																					f.id ===
																					file.id,
																			)
																		return (
																			matchingFile ||
																			matchingPreviousFile || {
																				id: file.id,
																				title: 'Unknown Resource',
																				resource_type:
																					'custom_resource',
																				url: '#',
																				syllabuses:
																					[],
																				stages: [],
																				years: [],
																				syllabus_types:
																					[],
																				officialreleasedate:
																					new Date().toISOString(),
																				filesize:
																					file.size ||
																					0,
																			}
																		)
																	},
																)

															// Combine and deduplicate
															return Array.from(
																new Map(
																	[
																		...remainingPrevFiles,
																		...newlySelectedFiles,
																	].map(
																		(
																			item,
																		) => [
																			item.id,
																			item,
																		],
																	),
																).values(),
															)
														},
													)
												}}
												onPageChange={
													resetPageAndScroll
												}
												isIncludeCopyUrlOnTitle
												showNextToTitleTooltip={
													isScreenDownLg ||
													isFilterShown
												}
												showFileSizeUnderTitle
												initialSortModel={
													selectedSortModel
												}
												selectionModel={
													selectedAssetIds
												}
												paginationMode="server"
												rowCount={
													resourcesResponse.json
														.total_records
												}
												showSelectAllCheckbox
												keepNonExistentRowsSelected
												onSortModelChange={
													handleSortModelChange
												}
												sortingMode="server"
												syllabusContext={
													syllabusContext
												}
											/>
										)}
								</>
							) : isFetchingResources ? null : (
								<h4 className="text-center mt-20">
									{/* eslint-disable-next-line quotes */}
									{"We didn't find any results. "}
									<button
										type="reset"
										className="underline bold nsw-text--brand-dark"
										onClick={handleClearAll}
									>
										Clear all
									</button>
								</h4>
							)}

							{isFetchingResources && (
								<div className="bg-white/90 absolute inset-0 w-full flex justify-center pt-6">
									<Loading />
								</div>
							)}
						</div>
					</GridCol>
				</GridWrapper>
			</div>
			<GeneratingOverlayCommon
				modalStatus={isDownloading}
				handleCancel={handleCancelDownloadSelected}
			/>
			{errorMessageOnDownload && (
				<CustomModal
					title="Error"
					modalStatus={!!errorMessageOnDownload}
					handleCancel={() => setErrorMessageOnDownload('')}
					hideConfirmButton
				>
					<p>{errorMessageOnDownload}</p>
				</CustomModal>
			)}
		</>
	)
}
export default WpResources
